import React, { useState } from 'react';
import {
  PlanogramComplianceConfigItem,
  PlanogramComplianceResponse,
  RogItemStatus,
} from '../../../types';
import { getFilteredBoxesForLayer, LayerType } from '../../../utils/get-filtered-bboxex-for-layer';
import { LayerGroup } from './ActionComplianceGroup';
import { PlanogramComplianceAdvancedSettings } from './PlanogramComplianceAdvancedSettings';
import { PlanogramComplianceToggles } from './PlanogramComplianceToggles';

interface PlanogramComplianceControlCenterProps {
  onRunModel: () => void;
  planogramComplianceData?: PlanogramComplianceResponse | null;
  isLoading?: boolean;
  currentImageId?: number;
  currentImageElement?: HTMLImageElement;
  onEnabledLayersChange?: (enabledLayers: Set<string>) => void;
  enabledLayers: Set<string>;
  selectedIlItemIds?: Set<number>;
  isDataFromCache?: boolean;
  onClearCache?: () => void;
  isV2: boolean;
  onIsV2Change: (isV2: boolean) => void;
  config: PlanogramComplianceConfigItem[];
  defaultConfig?: PlanogramComplianceConfigItem[] | null;
  onConfigChange: (key: string, value: number | boolean | string | string[]) => void;
  onConfigReset?: () => void;
}

interface LayerInfo {
  key: LayerType;
  label: string;
  color: string;
  bboxColor: string;
}

export const ACTION_LAYER_CONFIG: LayerInfo[] = [
  {
    key: RogItemStatus.CORRECT,
    label: 'Correct',
    color: 'bg-green-500',
    bboxColor: '#1a8542ff',
  },
  {
    key: RogItemStatus.MOVE,
    label: 'Move',
    color: 'bg-yellow-500',
    bboxColor: '#eab308',
  },
  {
    key: RogItemStatus.OUT,
    label: 'Out',
    color: 'bg-red-500',
    bboxColor: '#da4444ff',
  },
  {
    key: RogItemStatus.REMOVE,
    label: 'Remove',
    color: 'bg-purple-500',
    bboxColor: '#a855f7',
  },
  {
    key: RogItemStatus.UNKNOWN,
    label: 'Unknown',
    color: 'bg-black',
    bboxColor: '#000',
  },
];

export const COMPLIANCE_COLOR = {
  compliant: '#16a34a', // green
  non_compliant: '#eab308', // yellow
  out: '#ef4444', // red
  faceover: '#a855f7', // purple
  unknown: '#000', // black
};

export const PlanogramComplianceControlCenter: React.FC<PlanogramComplianceControlCenterProps> = ({
  onRunModel,
  planogramComplianceData,
  isLoading = false,
  currentImageId,
  currentImageElement,
  onEnabledLayersChange,
  enabledLayers,
  selectedIlItemIds,
  isDataFromCache = false,
  onClearCache,
  isV2,
  onIsV2Change,
  config,
  defaultConfig,
  onConfigChange,
  onConfigReset,
}) => {
  const [searchText, setSearchText] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<LayerType>>(new Set());

  // Check if planogram compliance data is from a different image
  const isDataFromDifferentImage =
    planogramComplianceData &&
    currentImageId !== undefined &&
    planogramComplianceData.imageId !== currentImageId;

  const toggleLayer = (layerKey: LayerType) => {
    const newSet = new Set(enabledLayers);
    if (newSet.has(layerKey)) {
      newSet.delete(layerKey);
    } else {
      newSet.add(layerKey);
    }
    // Notify parent component of layer changes
    onEnabledLayersChange?.(newSet);
  };

  const toggleGroupCollapse = (layerKey: LayerType) => {
    setCollapsedGroups((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(layerKey)) {
        newSet.delete(layerKey);
      } else {
        newSet.add(layerKey);
      }
      return newSet;
    });
  };

  const getLayerCount = (layerKey: LayerType): number => {
    return planogramComplianceData
      ? getFilteredBoxesForLayer(layerKey, planogramComplianceData, selectedIlItemIds).length
      : 0;
  };

  const renderLayerElements = (layerKey: LayerType) => {
    if (!planogramComplianceData || !enabledLayers.has(layerKey)) return null;

    const filteredData = getFilteredBoxesForLayer(
      layerKey,
      planogramComplianceData,
      selectedIlItemIds
    );
    if (!filteredData.length) return null;

    const layerInfo = ACTION_LAYER_CONFIG.find((l) => l.key === layerKey);
    if (!layerInfo) return null;

    return (
      <LayerGroup
        key={layerKey}
        layerKey={layerKey}
        layerLabel={layerInfo.label}
        layerColor={layerInfo.color}
        filteredData={filteredData}
        totalCount={filteredData.length}
        searchText={searchText}
        isCollapsed={collapsedGroups.has(layerKey)}
        onToggleCollapse={() => toggleGroupCollapse(layerKey)}
        cameraImageElement={currentImageElement}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Run Model Button and Cache Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={onRunModel}
            disabled={isLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-md font-medium flex items-center gap-2"
          >
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            )}
            {isLoading ? 'Running Model...' : 'Run Model'}
          </button>

          <PlanogramComplianceToggles
            isV2={isV2}
            onIsV2Change={onIsV2Change}
            isDataFromCache={isDataFromCache && !!planogramComplianceData}
            showCacheIndicator={true}
          />
        </div>

        {onClearCache && (
          <button
            onClick={onClearCache}
            className="text-gray-600 hover:text-gray-800 hover:bg-gray-100 px-3 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors"
            title="Clear all cached model results"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
            Clear Cache
          </button>
        )}
      </div>

      {/* Advanced Settings Panel */}
      <div className={isV2 ? 'hidden' : ''}>
        <PlanogramComplianceAdvancedSettings
          config={config}
          defaultConfig={defaultConfig}
          onConfigChange={onConfigChange}
          onReset={() => onConfigReset?.()}
          isV2={isV2}
        />
      </div>

      {/* Warning for different image data */}
      {isDataFromDifferentImage && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Data from Different Image</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  The product recognition data shown is from image ID{' '}
                  <strong>{planogramComplianceData?.imageId}</strong>, but you're currently viewing
                  image ID <strong>{currentImageId}</strong>. Run the model again to get data for
                  the current image.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {planogramComplianceData && (
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Left Sidebar - Layers List */}
          <div className="lg:col-span-1 flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Layers</h3>
              <button
                onClick={() => {
                  const allLayersEnabled = ACTION_LAYER_CONFIG.every((layer) =>
                    enabledLayers.has(layer.key)
                  );
                  const newSet = new Set<string>();

                  if (!allLayersEnabled) {
                    // Not all layers are enabled, so enable all
                    ACTION_LAYER_CONFIG.forEach((layer) => newSet.add(layer.key));
                  }
                  // If all layers are enabled, leave newSet empty to disable all

                  onEnabledLayersChange?.(newSet);
                }}
                className="p-1.5 rounded-md transition-colors bg-gray-100 hover:bg-gray-200 text-gray-600"
                title={
                  ACTION_LAYER_CONFIG.every((layer) => enabledLayers.has(layer.key))
                    ? 'Disable all layers'
                    : 'Enable all layers'
                }
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {ACTION_LAYER_CONFIG.every((layer) => enabledLayers.has(layer.key)) ? (
                    // "Uncheck all" icon - square with X
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  ) : (
                    // "Check all" icon - checkmark
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  )}
                </svg>
              </button>
            </div>
            <div className="space-y-2">
              {ACTION_LAYER_CONFIG.map((layer) => {
                const count = getLayerCount(layer.key);
                const isEnabled = enabledLayers.has(layer.key);

                return (
                  <button
                    key={layer.key}
                    onClick={() => toggleLayer(layer.key)}
                    className={`w-full text-left p-3 rounded-md border transition-colors ${
                      isEnabled
                        ? 'border-blue-300 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full ${layer.color} mr-2`}></div>
                        <span className="text-sm font-medium">{layer.label}</span>
                      </div>
                      <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                        {count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right Column - Layer Elements */}
          <div className="lg:col-span-3 flex flex-col">
            <h3 className="text-lg font-semibold mb-4">Elements</h3>

            {/* Search Field */}
            <div className="mb-4">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search by OCR text, product names, UPC, or item numbers..."
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    <svg
                      className="h-5 w-5 text-gray-400 hover:text-gray-600"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            </div>

            <div
              className="overflow-y-auto"
              style={{ height: 'calc(9 * 3.75rem + 8 * 0.5rem - 4rem)' }}
            >
              {ACTION_LAYER_CONFIG.map((layer) => renderLayerElements(layer.key))}
              {ACTION_LAYER_CONFIG.every(
                (layer) => !enabledLayers.has(layer.key) || getLayerCount(layer.key) === 0
              ) && (
                <div className="text-center text-gray-500 py-8">
                  No elements to display. Enable layers or run the model first.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {!planogramComplianceData && (
        <div className="text-center text-gray-500 py-8">
          Click "Run Model" to generate product recognition data.
        </div>
      )}
    </div>
  );
};
